import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { notificationsApi } from '@/api/client';
import { CrewIcon, useCssHeightVar, type CrewIconName } from './CrewUi';
import { useAuth } from '@/auth/AuthContext';
import { filterNavByPermission, portalNavItems } from '@/pages/navItems';
import { Button } from './Button';
import { BrandMark } from './BrandMark';
import { NotificationsBell } from './NotificationsBell';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  /** Sidebar section heading; a heading is shown when the group changes. */
  group?: string;
}

interface NavProps {
  items: NavItem[];
  variant?: 'sidebar' | 'top' | 'crew';
}

/**
 * Portal top nav with permission filtering.
 * Defense-in-depth: Client Viewer never sees Book; Client Sub / Viewer never see Users
 * even if a stale JWT still carries elevated portal flags from an older role seed.
 */
export function PortalNav({ variant = 'top' }: { variant?: 'sidebar' | 'top' | 'crew' }) {
  const { hasPermission, user } = useAuth();
  const roleBlocksUserMgmt = user?.role === 'Client Sub' || user?.role === 'Client Viewer';
  const roleBlocksBook = user?.role === 'Client Viewer';
  const items = filterNavByPermission(portalNavItems, (permission) => {
    if (permission === 'portal.manage_users' && roleBlocksUserMgmt) return false;
    if (permission === 'portal.book' && roleBlocksBook) return false;
    return hasPermission(permission);
  });
  return <Nav items={items} variant={variant} />;
}

function clearCrewGuidedFlag() {
  try {
    sessionStorage.removeItem('crew-guided');
  } catch {
    /* private mode */
  }
}

/** Fired after notifications are marked read so the crew Alerts badge refreshes. */
export const NOTIFICATIONS_CHANGED = 'dispatchdash:notifications-changed';

const CREW_TAB_ICONS: Record<string, CrewIconName> = {
  '/crew': 'trip',
  '/crew/scan': 'scan',
  '/crew/pod': 'sign',
  '/crew/costs': 'costs',
  '/crew/earnings': 'pay',
  '/notifications': 'alerts',
};

/** Crew bottom tab bar: icon + label tabs, clear selected state, unread badge on Alerts. */
function CrewTabBar({ items }: { items: NavItem[] }) {
  const ref = useRef<HTMLElement>(null);
  useCssHeightVar(ref, '--crew-tabbar-h');
  const [unread, setUnread] = useState(0);
  const loadUnread = useCallback(() => {
    notificationsApi
      .list(true)
      .then((rows) => setUnread(Array.isArray(rows) ? rows.length : 0))
      .catch(() => {
        /* offline — keep the last count */
      });
  }, []);
  useEffect(() => {
    loadUnread();
    window.addEventListener(NOTIFICATIONS_CHANGED, loadUnread);
    return () => window.removeEventListener(NOTIFICATIONS_CHANGED, loadUnread);
  }, [loadUnread]);

  return (
    <nav ref={ref} className="nav nav-crew" aria-label="Crew">
      {items.map((item) => {
        const badge = item.to === '/notifications' && unread > 0 ? (unread > 9 ? '9+' : String(unread)) : null;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            aria-label={badge ? `${item.label} (${unread} unread)` : undefined}
            onClick={clearCrewGuidedFlag}
          >
            <span className="nav-crew-icon">
              <CrewIcon name={CREW_TAB_ICONS[item.to] ?? 'trip'} className="" />
              {badge && <span className="nav-crew-badge">{badge}</span>}
            </span>
            <span className="nav-crew-label">{item.label}</span>
          </NavLink>
        );
      })}
      <style>{`
        .nav-crew {
          /* Bottom tab bar: reachable with a thumb, big targets for gloves. */
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 30;
          display: grid;
          grid-auto-flow: column;
          grid-auto-columns: minmax(0, 1fr);
          width: 100%;
          box-sizing: border-box;
          padding: 6px 2px calc(6px + env(safe-area-inset-bottom));
          background: var(--crew-raised, #1A2128);
          border-top: 1px solid var(--crew-border, #34404B);
        }
        .nav-crew .nav-link {
          min-width: 0;
          min-height: 60px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          padding: 2px 0;
          color: var(--crew-muted, #C3CBD3);
          font-weight: 600;
          font-size: 0.875rem;
          line-height: 1.15;
          text-decoration: none;
        }
        .nav-crew .nav-link:hover { text-decoration: none; }
        .nav-crew-icon {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          max-width: 100%;
          height: 32px;
          border-radius: 16px;
        }
        .nav-crew-icon svg { width: 26px; height: 26px; }
        .nav-crew-label {
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .nav-crew .nav-link.active { color: var(--crew-signal, #FFD100); font-weight: 700; }
        .nav-crew .nav-link.active .nav-crew-icon {
          background: var(--crew-signal, #FFD100);
          color: var(--crew-bg, #0E1216);
        }
        .nav-crew-badge {
          position: absolute;
          top: -6px;
          left: 50%;
          min-width: 18px;
          height: 22px;
          padding: 0 4px;
          border-radius: 11px;
          background: var(--crew-urgent, #C0392B);
          color: #fff;
          font-size: 0.75rem;
          font-weight: 700;
          line-height: 22px;
          text-align: center;
          border: 2px solid var(--crew-raised, #1A2128);
          box-sizing: content-box;
        }
      `}</style>
    </nav>
  );
}

export function Nav({ items, variant = 'sidebar' }: NavProps) {
  if (variant === 'crew') return <CrewTabBar items={items} />;
  return (
    <nav className={`nav nav-${variant}`}>
      {items.map((item, i) => (
        <Fragment key={item.to}>
        {variant === 'sidebar' && item.group && item.group !== items[i - 1]?.group && (
          <div className="nav-group-label">{item.group}</div>
        )}
        <NavLink
          to={item.to}
          end={item.end}
          className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
        >
          {item.label}
        </NavLink>
        </Fragment>
      ))}
      <style>{`
        .nav-sidebar {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .nav-group-label {
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--color-text-muted);
          padding: var(--space-md) var(--space-md) var(--space-xs);
        }
        .nav-group-label:first-child { padding-top: 0; }
        .nav-sidebar .nav-link {
          display: block;
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          color: var(--color-graphite);
          font-size: 0.875rem;
          font-weight: 500;
          text-decoration: none;
        }
        .nav-sidebar .nav-link:hover {
          background: var(--color-fog);
          text-decoration: none;
        }
        .nav-sidebar .nav-link.active {
          background: var(--color-ink);
          color: var(--color-white);
        }
        .nav-top {
          display: flex;
          gap: var(--space-sm);
          flex-wrap: wrap;
        }
        .nav-top .nav-link {
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          color: var(--color-text-muted);
          font-size: 0.875rem;
          font-weight: 500;
          text-decoration: none;
        }
        .nav-top .nav-link.active {
          color: var(--color-ink);
          background: var(--color-fog);
          box-shadow: inset 0 -2px 0 var(--color-signal);
        }
      `}</style>
    </nav>
  );
}

export function AppHeader({
  onMenuToggle,
  menuOpen = false,
}: {
  onMenuToggle?: () => void;
  menuOpen?: boolean;
} = {}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="app-header">
      <div className="header-brand">
        {onMenuToggle && (
          <button
            type="button"
            className="header-menu-btn"
            onClick={onMenuToggle}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="app-sidebar"
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        )}
        <BrandMark variant="light" showLegal={false} size="sm" />
        <span className="header-rule" aria-hidden />
        <span className="header-product">DispatchDash</span>
      </div>
      <div className="header-user">
        <NotificationsBell />
        <span className="header-name">{user?.name}</span>
        <span className="header-role">{user?.role}</span>
        <Button variant="ghost" size="sm" onClick={handleLogout}>Sign out</Button>
      </div>
      <style>{`
        .app-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: var(--header-height);
          padding: 0 var(--space-lg);
          background: var(--color-ink);
          color: var(--color-white);
          border-bottom: 1px solid #1A2026;
        }
        .header-brand {
          display: flex;
          align-items: center;
          gap: var(--space-md);
        }
        .header-rule {
          width: 2px;
          height: 22px;
          background: var(--color-signal);
          flex-shrink: 0;
        }
        .header-product {
          font-family: var(--font-display);
          font-weight: 700;
          font-size: 0.9375rem;
          letter-spacing: -0.01em;
        }
        .header-user {
          display: flex;
          align-items: center;
          gap: var(--space-md);
        }
        .header-name { font-weight: 500; font-size: 0.875rem; }
        .header-menu-btn {
          display: none;
          background: none;
          border: none;
          color: var(--color-white);
          font-size: 1.375rem;
          line-height: 1;
          min-width: 44px;
          min-height: 44px;
          margin-left: calc(-1 * var(--space-sm));
          cursor: pointer;
        }
        @media (max-width: 768px) {
          .app-header { padding: 0 var(--space-md); }
          .header-menu-btn { display: inline-flex; align-items: center; justify-content: center; }
          .header-rule, .header-product, .header-name, .header-role { display: none; }
          .header-user { gap: var(--space-sm); }
        }
        .header-role {
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 3px 8px;
          background: rgba(255,255,255,0.08);
          border-radius: var(--radius-sm);
          color: var(--color-steel);
        }
        .app-header .btn-ghost {
          color: var(--color-white);
        }
        .app-header .btn-ghost:not(:disabled):hover {
          background: rgba(255,255,255,0.08);
        }
      `}</style>
    </header>
  );
}
